/**
 * Master Data Edit (handler)
 * Encapsulates modal state + API calls.
 */

import type { ZodTypeAny } from "zod";
import { notifier } from "../core/notifier/notifier";
import * as brandsSchemas from "../api/generated/brands.zod";
import * as productsSchemas from "../api/generated/products.zod";
import * as usersSchemas from "../api/generated/users.zod";
import { attachEnterEscapeNavigation } from "../core/keyboard-navigation/keyboard-navigation";
import { CommonUtils } from "../util/common-utils";
import { phpProductPaths, phpUrl } from "../core/php-data-access";
import { UrlExtractor } from "../util/url/url-extractor";
import { OgpRender } from "../util/ogp/ogp-render";
import { spinner } from "../util/spinner/spinner";
import { formValidator } from "../util/form-validator";
import { Roles } from "../constants/roles";
import { ChoicesControl } from "../components/choices-control/choices-control-setup";
import { OptionSelector } from "../components/option-selector/option-selector";
import { ScrollLock } from "../core/scroll-lock";
import { ModalNestedPosition } from "../util/modal-nested-position";
import { CategoryHierarchyTreeUtils } from "../search/category/hierarchy/category-hierarchy-tree-utils";
import { SearchCategoryModalSetup } from "../search/category/modal/search-category-modal-setup";
import { ChipPickerRender } from "../components/picker/chips/chip-picker-render";
import { ModalSearchSelectionIO } from "../components/modal-search/modal-search-selection-io";
import { registerMasterDataEditUsersListener } from "../user/edit/user-edit-setup";
import {
    applyUserPrefecturePendingValueForMasterDataEdit,
    clearUserPrefecturePendingValueForMasterDataEdit,
    closeUserPrefectureModalForMasterDataEdit,
    getUserPrefectureLabelForMasterDataEdit,
    getUserPrefectureOptionForMasterDataEdit,
    getUserPrefectureOptionsForMasterDataEdit,
    lookupUserAddressByZipCodeForMasterDataEdit,
    normalizeUserPrefectureMatchingTextForMasterDataEdit,
    openUserPrefectureModalForMasterDataEdit,
    renderUserPrefectureDisplayForMasterDataEdit,
    renderUserPrefectureModalListForMasterDataEdit,
    resolveUserPrefectureFromLookupPayloadForMasterDataEdit,
    translateUserPrefectureLabelForMasterDataEdit,
} from "../user/edit/user-edit-handler.js";
import {
    qs,
    getClosest,
    getRoleButton,
    getRoleElement,
    getRoleElements,
    getRoleImage,
    getRoleScript,
    getRoleSelect,
    getSubmitControl,
    getOptionByValue,
    setValue,
    getFormDataObject,
    type FormDataObject,
} from "./master-data-edit-form";
import { buildUserFullName, normalizePayload, toDatetimeLocalValue } from "./master-data-edit-utils";
import {
    handleResponse,
    type MasterDataEditApiResponse,
    requestCreateItem,
    requestDeleteItem,
    requestUpdateItem,
    requestUploadImage,
    resolveMasterUrl,
} from "./master-data-edit-api.js";
import {
    bindMasterDataBlur,
    bindMasterDataChange,
    bindMasterDataClick,
    bindMasterDataInput,
    bindMasterDataKeydown,
    bindMasterDataSubmit,
} from "./master-data-edit-modal-listener";
import { MasterDataEditRender } from "./master-data-edit-render";
import { CardPreviewTileRender } from "../sns/preview-manager/render/card-preview/card-preview-tile-render";

type MasterDataEditContextRecord = FormDataObject;

type MasterDataEditMode = "edit" | "create";

type MasterDataEditKeyboardNav = {
    capture: () => void;
    focusFirstText: () => void;
    clear: () => void;
    detach: () => void;
};

type ProductCategoryNode = {
    id: number;
    name: string;
};

type ProductCategoryTree = {
    parents: ProductCategoryNode[];
    children_by_parent: Record<string, ProductCategoryNode[]>;
    parent_by_category: Record<string, number>;
};

type OgpState = {
    render: OgpRender | null;
    timer: ReturnType<typeof setTimeout> | null;
    lastUrl: string;
    lastAutoImage: string;
};

type BrandSnsOgpState = {
    timers: Record<string, ReturnType<typeof setTimeout> | null>;
    lastUrlByField: Record<string, string>;
};

type MasterDataEditSuccessAction = "saved" | "deleted" | "uploaded";

type MasterDataEditContext = {
    resource: string;
    item?: MasterDataEditContextRecord | null;
    defaults?: MasterDataEditContextRecord;
    specDefaults?: MasterDataEditContextRecord;
    urls?: Record<string, string>;
    [key: string]: unknown;
};

function asRecord(value: unknown): MasterDataEditContextRecord {
    return value && typeof value === "object" ? (value as MasterDataEditContextRecord) : {};
}

function resolveMasterDataEditSchema(resource: string, mode: MasterDataEditMode): ZodTypeAny | null {
    if (resource === "users") {
        return mode === "create" ? usersSchemas.CreateItemBody : usersSchemas.UpdateItemBody;
    }

    if (resource === "brands") {
        return mode === "create" ? brandsSchemas.CreateItemBody : brandsSchemas.UpdateItemBody;
    }

    if (resource === "products") {
        return mode === "create" ? productsSchemas.CreateItemBody : productsSchemas.UpdateItemBody;
    }

    return null;
}

export class MasterDataEditController {
    public ctx: MasterDataEditContext;

    public resource: string;

    public urls: Record<string, string>;

    public modal: HTMLElement | null;

    public overlay: Element | null;

    public closeBtns: HTMLElement[];

    public form: HTMLFormElement | null;

    public mode: MasterDataEditMode;

    public keyboardNav: MasterDataEditKeyboardNav | null;

    public countryFlagsMap: Record<string, string>;

    public brandUrlOgp: OgpState;

    public brandSnsOgp: BrandSnsOgpState;

    public productUrlOgp: OgpState;

    public productImageObjectUrl: string;

    public productImagePreviewBust: number;

    public brandImageObjectUrl: string;

    public brandImagePreviewBust: number;

    public _userPrefecturePendingValue: string;

    private _modalEventsBound: boolean;

    private _productCategoryHierarchyBound: boolean;

    private _usersListenersBound: boolean;

    private _productCategoryModalBound: boolean;

    private _productCategoryModalActiveParentId: number | null;

    private _productCategoryTree: ProductCategoryTree | null;

    public createModeSlot(): MasterDataEditMode {
        return "edit";
    }

    public createKeyboardNavSlot(): MasterDataEditKeyboardNav | null {
        return null;
    }

    public createCountryFlagsMap(): Record<string, string> {
        return {};
    }

    public createProductImageObjectUrlSlot(): string {
        return "";
    }

    public createProductImagePreviewBust(): number {
        return 0;
    }

    public createFalseFlag(): boolean {
        return false;
    }

    public createNullableNumberSlot(): number | null {
        return null;
    }

    public createProductCategoryTreeSlot(): ProductCategoryTree | null {
        return null;
    }

    public createChildrenByParentMap(): Record<string, ProductCategoryNode[]> {
        return {};
    }

    public createParentByCategoryMap(): Record<string, number> {
        return {};
    }

    public constructor(ctx: MasterDataEditContext, modal: HTMLElement | null = null) {
        this.ctx = ctx;
        this.resource = ctx.resource;
        this.urls = ctx.urls || {};

        this.modal = modal || qs(document, `[data-role="${Roles.MASTER_DATA_MODAL}"]`);
        this.overlay = this.modal ? qs(this.modal, `[data-role="${Roles.MASTER_DATA_MODAL_OVERLAY}"]`) : null;
        this.closeBtns = this.modal ? getRoleElements(this.modal, Roles.CLOSE_MASTER_DATA_MODAL) : [];
        const formCandidate = this.modal ? qs(this.modal, `form[data-role="${Roles.MASTER_DATA_FORM}"]`) : null;
        this.form = formCandidate instanceof HTMLFormElement ? formCandidate : null;

        this.mode = this.createModeSlot();

        this.submit = this.submit.bind(this);

        this.keyboardNav = this.createKeyboardNavSlot();

        this.countryFlagsMap = this.createCountryFlagsMap();

        this.brandUrlOgp = {
            render: this.resource === "brands" ? new OgpRender() : null,
            timer: null,
            lastUrl: "",
            lastAutoImage: "",
        };

        this.brandSnsOgp = {
            timers: {},
            lastUrlByField: {},
        };

        this.productUrlOgp = {
            render: this.resource === "products" ? new OgpRender() : null,
            timer: null,
            lastUrl: "",
            lastAutoImage: "",
        };

        if (this.resource === "brands" && this.modal instanceof HTMLElement) {
            const mapEl = getRoleScript(this.modal, Roles.COUNTRY_FLAGS_MAP);
            if (mapEl instanceof HTMLScriptElement) {
                try {
                    const parsed = JSON.parse(mapEl.textContent || "{}") as MasterDataEditContextRecord;
                    if (parsed && typeof parsed === "object") {
                        const normalized = this.createCountryFlagsMap();
                        Object.entries(parsed).forEach(([key, value]) => {
                            if (typeof value === "string") {
                                normalized[key] = value;
                            }
                        });
                        this.countryFlagsMap = normalized;
                    }
                } catch {
                    this.countryFlagsMap = {};
                }
            }
        }

        this.productImageObjectUrl = this.createProductImageObjectUrlSlot();
        this.productImagePreviewBust = this.createProductImagePreviewBust();
        this.brandImageObjectUrl = this.createProductImageObjectUrlSlot();
        this.brandImagePreviewBust = this.createProductImagePreviewBust();

        this._modalEventsBound = this.createFalseFlag();
        this._productCategoryHierarchyBound = this.createFalseFlag();
        this._usersListenersBound = this.createFalseFlag();
        this._userPrefecturePendingValue = this.createProductImageObjectUrlSlot();
        this._productCategoryModalBound = this.createFalseFlag();
        this._productCategoryModalActiveParentId = this.createNullableNumberSlot();
        this._productCategoryTree = this.createProductCategoryTreeSlot();
    }

    public getProductCategoryTreeFromDom(): ProductCategoryTree | null {
        if (this.resource !== "products") return null;
        if (!(this.modal instanceof HTMLElement)) return null;

        const jsonEl = getRoleScript(this.modal, Roles.PRODUCT_CATEGORY_TREE_JSON);
        if (!(jsonEl instanceof HTMLScriptElement)) return null;

        try {
            const parsed = JSON.parse(jsonEl.textContent || "null") as MasterDataEditContextRecord | null;
            if (!parsed || typeof parsed !== "object") return null;

            const parentsRaw = Array.isArray(parsed.parents) ? parsed.parents : [];
            const parents: ProductCategoryNode[] = [];
            parentsRaw.forEach((parent) => {
                if (!parent || typeof parent !== "object") return;
                const id = Number(Reflect.get(parent, "id"));
                const name = String(Reflect.get(parent, "name") ?? "").trim();
                if (!Number.isFinite(id) || !name) return;
                parents.push({ id, name });
            });

            const childrenByParent = this.createChildrenByParentMap();
            const rawChildrenByParent = parsed.children_by_parent;
            if (rawChildrenByParent && typeof rawChildrenByParent === "object") {
                Object.entries(rawChildrenByParent as Record<string, unknown>).forEach(([parentId, list]) => {
                    if (!Array.isArray(list)) return;
                    const children: ProductCategoryNode[] = [];
                    list.forEach((child) => {
                        if (!child || typeof child !== "object") return;
                        const id = Number(Reflect.get(child, "id"));
                        const name = String(Reflect.get(child, "name") ?? "").trim();
                        if (!Number.isFinite(id) || !name) return;
                        children.push({ id, name });
                    });
                    childrenByParent[String(parentId)] = children;
                });
            }

            const parentByCategory = this.createParentByCategoryMap();
            const rawParentByCategory = parsed.parent_by_category;
            if (rawParentByCategory && typeof rawParentByCategory === "object") {
                Object.entries(rawParentByCategory as Record<string, unknown>).forEach(([categoryId, parentId]) => {
                    const normalizedParentId = Number(parentId);
                    if (!Number.isFinite(normalizedParentId)) return;
                    parentByCategory[String(categoryId)] = normalizedParentId;
                });
            }

            return {
                parents,
                children_by_parent: childrenByParent,
                parent_by_category: parentByCategory,
            };
        } catch {
            return null;
        }
    }

    public syncProductCategoryHierarchy(selectedCategoryId: number | null | undefined = null): void {
        if (this.resource !== "products") return;
        if (!(this.modal instanceof HTMLElement)) return;
        if (!(this.form instanceof HTMLFormElement)) return;

        const pickerEl = getRoleElement(this.modal, Roles.PRODUCT_CATEGORY_PICKER);
        const displayEl = getRoleElement(this.modal, Roles.PRODUCT_CATEGORY_DISPLAY);
        const hiddenEl = this.form.elements.namedItem("category_id");
        const hasModalUi =
            pickerEl instanceof HTMLElement && displayEl instanceof HTMLElement && hiddenEl instanceof HTMLInputElement;
        if (hasModalUi) {
            if (!this._productCategoryTree) {
                this._productCategoryTree = this.getProductCategoryTreeFromDom();
            }
            const tree = this._productCategoryTree;
            if (!tree) return;

            const normalizeId = (v: unknown): number | null => {
                if (v === null || v === undefined) return null;
                if (v === "") return null;
                const n = Number(v);
                return Number.isFinite(n) && n > 0 ? n : null;
            };

            const selectedId = normalizeId(selectedCategoryId) || normalizeId(hiddenEl.value);
            hiddenEl.value = selectedId ? String(selectedId) : "";

            const nameById = new Map<number, string>();
            (tree.parents || []).forEach((p) => nameById.set(Number(p.id), String(p.name || "")));
            Object.values(tree.children_by_parent || {}).forEach((list) => {
                (Array.isArray(list) ? list : []).forEach((c) => nameById.set(Number(c.id), String(c.name || "")));
            });

            const parentByCategory = tree.parent_by_category || {};
            const parentIdFromChild = selectedId ? normalizeId(parentByCategory[String(selectedId)]) : null;
            const isParentId = (id: number | null): boolean =>
                (tree.parents || []).some((p) => Number(p?.id) === Number(id));
            const parentId =
                parentIdFromChild ||
                (selectedId && isParentId(selectedId) ? selectedId : this._productCategoryModalActiveParentId);

            if (parentId) this._productCategoryModalActiveParentId = parentId;

            const parentName = parentId ? nameById.get(Number(parentId)) || "" : "";
            const childName = selectedId ? nameById.get(Number(selectedId)) || "" : "";
            const label = (() => {
                if (!selectedId) return "";
                if (parentId && selectedId !== parentId && parentName && childName) {
                    return `${parentName} > ${childName}`;
                }
                return childName || parentName || String(selectedId);
            })();

            ChipPickerRender.render({
                rootEl: displayEl,
                items: selectedId ? [{ id: String(selectedId), label }] : [],
                options: {
                    chipClass: "picker-chip",
                    labelClass: "picker-chip__label",
                    removeClass: "picker-chip__remove",
                    idAttribute: "data-category-id",
                    removeAction: "clear",
                    removeAriaLabel: "Clear",
                    emptyHtml:
                        '<span class="input-placeholder" data-translate="select_category">Select category</span>',
                },
            });
            return;
        }

        const parentSelect = getRoleSelect(this.modal, Roles.PRODUCT_CATEGORY_PARENT_SELECT);
        const childSelect = getRoleSelect(this.modal, Roles.PRODUCT_CATEGORY_CHILD_SELECT);
        if (!(parentSelect instanceof HTMLSelectElement)) return;
        if (!(childSelect instanceof HTMLSelectElement)) return;

        if (!this._productCategoryTree) {
            this._productCategoryTree = this.getProductCategoryTreeFromDom();
        }
        const tree = this._productCategoryTree;
        if (!tree) return;

        const normalizeId = (v: unknown): number | null => {
            if (v === null || v === undefined) return null;
            const n = Number(v);
            return Number.isFinite(n) && n > 0 ? n : null;
        };

        const selectedId = normalizeId(selectedCategoryId) || normalizeId(childSelect.value);
        const parentByCategory = tree.parent_by_category || {};
        const parentIdFromChild = selectedId ? normalizeId(parentByCategory[String(selectedId)]) : null;

        const isParentId = (id: number | null): boolean => tree.parents.some((p) => Number(p?.id) === Number(id));
        const parentId =
            parentIdFromChild || (selectedId && isParentId(selectedId) ? selectedId : normalizeId(parentSelect.value));

        parentSelect.value = parentId ? String(parentId) : "";
        this.rebuildProductCategoryChildOptions(parentId);

        if (selectedId) {
            const exists = Array.from(childSelect.options).some((opt) => opt.value === String(selectedId));
            childSelect.value = exists ? String(selectedId) : parentId ? String(parentId) : "";
        } else if (parentId) {
            childSelect.value = String(parentId);
        }
    }

    public rebuildProductCategoryChildOptions(parentId: number | null): void {
        if (!(this.modal instanceof HTMLElement)) return;
        if (!this._productCategoryTree) return;

        const childSelect = getRoleSelect(this.modal, Roles.PRODUCT_CATEGORY_CHILD_SELECT);
        if (!(childSelect instanceof HTMLSelectElement)) return;

        const tree = this._productCategoryTree;
        const existingPlaceholder = getOptionByValue(childSelect, "");
        const placeholderText = existingPlaceholder?.textContent || "Select category";
        const placeholderTranslate = existingPlaceholder?.getAttribute("data-translate") || "select_category";

        MasterDataEditRender.renderProductCategoryChildOptions(
            childSelect,
            tree,
            parentId,
            placeholderText,
            placeholderTranslate,
        );
    }

    public openProductCategoryModal(): void {
        if (this.resource !== "products") return;
        if (!(this.modal instanceof HTMLElement)) return;
        if (!(this.form instanceof HTMLFormElement)) return;

        if (!this._productCategoryTree) {
            this._productCategoryTree = this.getProductCategoryTreeFromDom();
        }
        const tree = this._productCategoryTree;
        if (!tree) return;

        const sharedModal = SearchCategoryModalSetup.init(this.modal);
        if (sharedModal) {
            const normalizedTree = CategoryHierarchyTreeUtils.fromEmbedded(tree);

            const parents = Array.isArray(normalizedTree.parents) ? normalizedTree.parents : [];
            if (parents.length === 0) return;

            const hiddenEl = this.form.elements.namedItem("category_id");
            const selectedIds = ModalSearchSelectionIO.parseIdsFromValue(
                hiddenEl instanceof HTMLInputElement ? hiddenEl.value : "",
                { mode: "single" },
            );

            sharedModal.setSelectionMode("single");
            sharedModal.setCloseOnSelect(true);
            sharedModal.setTree(normalizedTree);
            sharedModal.setSelectedIds(selectedIds);
            sharedModal.setOnSelectionChange((ids: string[]) => {
                const id = ModalSearchSelectionIO.firstId(ids);
                setValue(this.form, "category_id", id);
                this.syncProductCategoryHierarchy(id ? Number(id) || null : null);
            });
            sharedModal.open();
        }
    }

    public clearProductUrlOgpPreview(): void {
        if (this.productUrlOgp.timer) {
            clearTimeout(this.productUrlOgp.timer);
        }
        this.productUrlOgp.timer = null;
        this.productUrlOgp.lastUrl = "";
        this.productUrlOgp.lastAutoImage = "";

        if (!(this.modal instanceof HTMLElement)) return;

        const section = getRoleElement(this.modal, Roles.PRODUCT_URL_OGP_SECTION);
        const container = getRoleElement(this.modal, Roles.PRODUCT_URL_OGP_PREVIEW);
        if (container instanceof HTMLElement) {
            MasterDataEditRender.clearContainer(container);
        }
        if (section instanceof HTMLElement) {
            section.style.display = "none";
        }
    }

    public clearBrandUrlOgpPreview(): void {
        if (this.brandUrlOgp.timer) {
            clearTimeout(this.brandUrlOgp.timer);
        }
        this.brandUrlOgp.timer = null;
        this.brandUrlOgp.lastUrl = "";
        this.brandUrlOgp.lastAutoImage = "";

        if (!(this.modal instanceof HTMLElement)) return;

        const section = getRoleElement(this.modal, Roles.BRAND_URL_OGP_SECTION);
        const container = getRoleElement(this.modal, Roles.BRAND_URL_OGP_PREVIEW);
        if (container instanceof HTMLElement) {
            MasterDataEditRender.clearContainer(container);
        }
        if (section instanceof HTMLElement) {
            section.style.display = "none";
        }
    }

    public clearBrandSnsOgpPreviews(): void {
        Object.values(this.brandSnsOgp.timers).forEach((t) => {
            if (t) clearTimeout(t);
        });
        this.brandSnsOgp.timers = {};
        this.brandSnsOgp.lastUrlByField = {};

        if (!(this.modal instanceof HTMLElement)) return;

        ["facebook", "instagram", "youtube", "tiktok", "x"].forEach((field) => {
            const section = getRoleElement(this.modal, `${Roles.BRAND_SNS_OGP_SECTION_PREFIX}${field}`);
            const container = getRoleElement(this.modal, `${Roles.BRAND_SNS_OGP_PREVIEW_PREFIX}${field}`);
            if (container instanceof HTMLElement) MasterDataEditRender.clearContainer(container);
            if (section instanceof HTMLElement) section.style.display = "none";
        });
    }

    public scheduleBrandUrlOgpUpdate(raw: string): void {
        if (this.resource !== "brands") return;
        if (this.brandUrlOgp.timer) {
            clearTimeout(this.brandUrlOgp.timer);
        }
        this.brandUrlOgp.timer = setTimeout(() => {
            void this.updateBrandUrlOgpPreview(raw);
        }, 800);
    }

    public async updateBrandUrlOgpPreview(raw: string, force = false): Promise<void> {
        if (this.resource !== "brands") return;
        if (!(this.modal instanceof HTMLElement)) return;
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!this.brandUrlOgp.render) return;

        const normalizedForCheck = this.normalizeUrlForCheck("website", String(raw || ""));
        const url = UrlExtractor.cleanUrl(String(normalizedForCheck || ""));

        const section = getRoleElement(this.modal, Roles.BRAND_URL_OGP_SECTION);
        const container = getRoleElement(this.modal, Roles.BRAND_URL_OGP_PREVIEW);
        if (!(container instanceof HTMLElement)) return;

        if (!url) {
            MasterDataEditRender.clearContainer(container);
            if (section instanceof HTMLElement) section.style.display = "none";
            this.brandUrlOgp.lastUrl = "";
            return;
        }

        const isNewUrl = url !== this.brandUrlOgp.lastUrl;
        if (!isNewUrl && !force) return;

        if (isNewUrl) {
            this.brandUrlOgp.lastUrl = url;
            if (section instanceof HTMLElement) section.style.display = "block";
            MasterDataEditRender.clearContainer(container);
            await this.brandUrlOgp.render.fetchAndRender(url, container, "compact");
        } else if (force && container.childElementCount === 0) {
            if (section instanceof HTMLElement) section.style.display = "block";
            await this.brandUrlOgp.render.fetchAndRender(url, container, "compact");
        }

        if (section instanceof HTMLElement && container.childElementCount === 0) {
            section.style.display = "none";
        }

        const cached = this.brandUrlOgp.render.cache?.get(url) as MasterDataEditContextRecord | undefined;
        const ogpImage = cached && typeof cached === "object" ? String(cached.image || "").trim() : "";
        if (!ogpImage) return;

        const imageEl = this.form.elements.namedItem("image_url");
        if (!(imageEl instanceof HTMLInputElement)) return;

        const currentImage = String(imageEl.value || "").trim();
        const isDefaultNoImage = (() => {
            const rawValue = String(currentImage || "").trim();
            if (!rawValue) return true;

            const normalized = rawValue
                .replace(/^https?:\/\/[^/]+/i, "")
                .split("?")[0]
                .split("#")[0]
                .replace(/^\/+/, "")
                .replace(/\\/g, "/")
                .toLowerCase();

            return normalized === "media/products/no_image.jpg";
        })();

        const canOverwrite = !currentImage || currentImage === this.brandUrlOgp.lastAutoImage || isDefaultNoImage;
        if (!canOverwrite) return;

        imageEl.value = ogpImage;
        this.brandUrlOgp.lastAutoImage = ogpImage;
        imageEl.dispatchEvent(new Event("input", { bubbles: true }));
    }

    public scheduleBrandSnsOgpUpdate(field: string, raw: string): void {
        if (this.resource !== "brands") return;
        const key = String(field || "").trim();
        if (!key) return;

        const existing = this.brandSnsOgp.timers[key];
        if (existing) clearTimeout(existing);

        this.brandSnsOgp.timers[key] = setTimeout(() => {
            void this.updateBrandSnsOgpPreview(key, raw);
        }, 800);
    }

    public async updateBrandSnsOgpPreview(field: string, raw: string, force = false): Promise<void> {
        if (this.resource !== "brands") return;
        if (!(this.modal instanceof HTMLElement)) return;
        const f = String(field || "").trim();
        if (!["facebook", "instagram", "youtube", "tiktok", "x"].includes(f)) return;

        const normalizedForCheck = this.normalizeUrlForCheck(f, String(raw || ""));
        const url = UrlExtractor.cleanUrl(String(normalizedForCheck || ""));

        const section = getRoleElement(this.modal, `${Roles.BRAND_SNS_OGP_SECTION_PREFIX}${f}`);
        const container = getRoleElement(this.modal, `${Roles.BRAND_SNS_OGP_PREVIEW_PREFIX}${f}`);
        if (!(container instanceof HTMLElement)) return;

        if (!url) {
            MasterDataEditRender.clearContainer(container);
            if (section instanceof HTMLElement) section.style.display = "none";
            this.brandSnsOgp.lastUrlByField[f] = "";
            return;
        }

        const last = this.brandSnsOgp.lastUrlByField[f] || "";
        const isNewUrl = url !== last;
        if (!isNewUrl && !force) return;

        if (isNewUrl) {
            this.brandSnsOgp.lastUrlByField[f] = url;
            if (section instanceof HTMLElement) section.style.display = "block";
            MasterDataEditRender.clearContainer(container);
            CardPreviewTileRender.renderIndexSnsTilePreview(container, url);
        } else if (force && container.childElementCount === 0) {
            if (section instanceof HTMLElement) section.style.display = "block";
            CardPreviewTileRender.renderIndexSnsTilePreview(container, url);
        }

        if (section instanceof HTMLElement && container.childElementCount === 0) {
            section.style.display = "none";
        }
    }

    public async updateProductUrlOgpPreview(raw: string, force = false): Promise<void> {
        if (this.resource !== "products") return;
        if (!(this.modal instanceof HTMLElement)) return;
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!this.productUrlOgp.render) return;

        if (this.getSelectedProductImageFile()) {
            const section = getRoleElement(this.modal, Roles.PRODUCT_URL_OGP_SECTION);
            if (section instanceof HTMLElement) section.style.display = "none";
            return;
        }

        const url = UrlExtractor.cleanUrl(String(raw || ""));

        const section = getRoleElement(this.modal, Roles.PRODUCT_URL_OGP_SECTION);
        const container = getRoleElement(this.modal, Roles.PRODUCT_URL_OGP_PREVIEW);
        if (!(container instanceof HTMLElement)) return;

        if (!url) {
            MasterDataEditRender.clearContainer(container);
            if (section instanceof HTMLElement) section.style.display = "none";
            this.productUrlOgp.lastUrl = "";
            return;
        }

        const isNewUrl = url !== this.productUrlOgp.lastUrl;
        if (!isNewUrl && !force) return;

        if (isNewUrl) {
            this.productUrlOgp.lastUrl = url;
            if (section instanceof HTMLElement) section.style.display = "block";
            MasterDataEditRender.clearContainer(container);
            await this.productUrlOgp.render.fetchAndRender(url, container, "compact");
        } else if (force && container.childElementCount === 0) {
            if (section instanceof HTMLElement) section.style.display = "block";
            await this.productUrlOgp.render.fetchAndRender(url, container, "compact");
        }

        if (section instanceof HTMLElement && container.childElementCount === 0) {
            section.style.display = "none";
        }

        const cached = this.productUrlOgp.render.cache?.get(url) as MasterDataEditContextRecord | undefined;
        const ogpTitle = cached && typeof cached === "object" ? String(cached.ogp_title || "").trim() : "";
        const generalDescription =
            cached && typeof cached === "object" ? String(cached.general_description || "").trim() : "";
        const ogpImage = cached && typeof cached === "object" ? String(cached.image || "").trim() : "";

        const nameEl = this.form.elements.namedItem("product_name");
        if (ogpTitle && nameEl instanceof HTMLInputElement && String(nameEl.value || "").trim() === "") {
            nameEl.value = ogpTitle;
            nameEl.dispatchEvent(new Event("input", { bubbles: true }));
        }

        const generalDescriptionEl = this.form.elements.namedItem("general_description");
        if (
            generalDescription &&
            generalDescriptionEl instanceof HTMLTextAreaElement &&
            String(generalDescriptionEl.value || "").trim() === ""
        ) {
            generalDescriptionEl.value = generalDescription;
            generalDescriptionEl.dispatchEvent(new Event("input", { bubbles: true }));
        }

        const imageEl = this.form.elements.namedItem("image");
        if (!(imageEl instanceof HTMLInputElement)) return;

        const currentImage = String(imageEl.value || "").trim();
        const isDefaultNoImage = (() => {
            const rawValue = String(currentImage || "").trim();
            if (!rawValue) return true;

            const normalized = rawValue
                .replace(/^https?:\/\/[^/]+/i, "")
                .split("?")[0]
                .split("#")[0]
                .replace(/^\/+/, "")
                .replace(/\\/g, "/")
                .toLowerCase();

            return normalized === "media/products/no_image.jpg";
        })();

        const canOverwrite = !currentImage || currentImage === this.productUrlOgp.lastAutoImage || isDefaultNoImage;

        if (!ogpImage || !canOverwrite) return;

        imageEl.value = ogpImage;
        this.productUrlOgp.lastAutoImage = ogpImage;
        imageEl.dispatchEvent(new Event("input", { bubbles: true }));
    }

    public scheduleProductUrlOgpUpdate(raw: string): void {
        if (this.resource !== "products") return;
        if (this.productUrlOgp.timer) {
            clearTimeout(this.productUrlOgp.timer);
        }
        this.productUrlOgp.timer = setTimeout(() => {
            void this.updateProductUrlOgpPreview(raw);
        }, 800);
    }

    public normalizeUrlForCheck(field: string, raw: string): string {
        const value = (raw || "").trim();
        if (!value) return "";
        if (/^https?:\/\//i.test(value)) return value;

        const normalizedField = String(field || "").replace(/^social_/, "");

        const prefixByField: Record<string, string> = {
            website: "https://",
            facebook: "https://www.facebook.com/",
            instagram: "https://www.instagram.com/",
            youtube: "https://www.youtube.com/",
            tiktok: "https://www.tiktok.com/@",
            x: "https://x.com/",
            twitter: "https://x.com/",
        };

        const prefix = prefixByField[normalizedField] || "https://";
        return prefix + value;
    }

    public openUrl(url: string): void {
        MasterDataEditRender.openUrlInNewTab(url);
    }

    public setImagePreview(container: HTMLElement, img: HTMLImageElement, src: string): void {
        const normalized = (src || "").trim();
        if (!normalized) {
            img.removeAttribute("src");
            container.classList.remove("is-visible");
            container.classList.remove("is-broken");
            img.onload = null;
            img.onerror = null;
            return;
        }

        container.classList.add("is-visible");
        container.classList.remove("is-broken");

        const productBase = String(phpProductPaths()?.images?.urlPath || "")
            .trim()
            .replace(/\/$/, "");
        const fallback = productBase ? `${productBase}/no_image.jpg` : "";

        img.onload = () => container.classList.remove("is-broken");
        img.onerror = () => {
            if (fallback && img.src !== fallback) {
                img.src = fallback;
                return;
            }
            container.classList.add("is-broken");
        };
        img.src = normalized;
    }

    public resolveProductImageUrl(raw: string): string {
        const value = (raw || "").trim();
        if (!value) return "";
        if (/^https?:\/\//i.test(value)) return value;
        if (/^\/\//.test(value)) return `${location.protocol}${value}`;

        if (value.startsWith("/") || value.includes("/") || value.includes("\\")) {
            const normalized = value.replace(/\\/g, "/");
            const path = normalized.startsWith("/") ? normalized : `/${normalized}`;
            try {
                return new URL(path, location.origin).toString();
            } catch {
                return path;
            }
        }

        if (value.includes("/") || value.includes("\\")) return "";

        const base = String(phpProductPaths()?.images?.urlPath || "")
            .trim()
            .replace(/\/$/, "");
        if (!base) return "";

        const brandEl = this.form?.elements?.namedItem("brand_id");
        const brandId =
            brandEl instanceof HTMLInputElement || brandEl instanceof HTMLSelectElement
                ? String(brandEl.value || "").trim()
                : "";
        if (!brandId) return "";

        const suffix = `${brandId}/${value}`;

        try {
            let baseUrl: URL;
            if (/^https?:\/\//i.test(base)) {
                baseUrl = new URL(base.endsWith("/") ? base : `${base}/`);
            } else if (/^\/\//.test(base)) {
                baseUrl = new URL(`${location.protocol}${base.endsWith("/") ? base : `${base}/`}`);
            } else {
                const basePath = base.startsWith("/") ? base : `/${base}`;
                baseUrl = new URL(basePath.endsWith("/") ? basePath : `${basePath}/`, location.origin);
            }

            const u = new URL(suffix, baseUrl);
            if (this.productImagePreviewBust) {
                u.searchParams.set("v", String(this.productImagePreviewBust));
            }
            return u.toString();
        } catch {
            const basePath = base.startsWith("/") ? base : `/${base}`;
            const joined = `${basePath.replace(/\/+$/, "")}/${suffix}`;
            if (!this.productImagePreviewBust) return joined;
            const joiner = joined.includes("?") ? "&" : "?";
            return `${joined}${joiner}v=${this.productImagePreviewBust}`;
        }
    }

    public updateImageUrlPreview(): void {
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!(this.modal instanceof HTMLElement)) return;

        const input = this.form.elements.namedItem("image_url");
        if (!(input instanceof HTMLInputElement)) return;

        const container = getRoleElement(this.modal, Roles.IMAGE_URL_PREVIEW);
        const img = getRoleImage(this.modal, Roles.IMAGE_URL_PREVIEW_IMG);
        if (!(container instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

        // "image_url" stores a bare filename (e.g. "380.webp") resolved against
        // media/brands/ — not a full URL — unless it's still an external URL
        // left over from before that convention (see brand_image_path() /
        // ISS-04-111); phpUrl.brandImage() mirrors that same resolution here.
        const url = phpUrl.brandImage(input.value) || "";
        this.setImagePreview(container, img, this.applyBrandImagePreviewBust(url));
    }

    // Cache-bust the brand image preview after an upload overwrites the same
    // filename (e.g. re-uploading "12.jpg") — otherwise the browser keeps
    // showing the stale cached image.
    private applyBrandImagePreviewBust(url: string): string {
        if (!url || !this.brandImagePreviewBust) return url;
        const joiner = url.includes("?") ? "&" : "?";
        return `${url}${joiner}v=${this.brandImagePreviewBust}`;
    }

    public updateProductImagePreview(): void {
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!(this.modal instanceof HTMLElement)) return;

        const input = this.form.elements.namedItem("image");
        if (!(input instanceof HTMLInputElement)) return;

        const container = getRoleElement(this.modal, Roles.IMAGE_PATH_PREVIEW);
        const img = getRoleImage(this.modal, Roles.IMAGE_PATH_PREVIEW_IMG);
        if (!(container instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

        const url = this.resolveProductImageUrl(input.value);
        this.setImagePreview(container, img, url);
    }

    public deriveBrandIndexFromName(name: string): string {
        const s = (name || "").trim();
        if (!s) return "";
        const ch = s[0];
        if (/[A-Za-z]/.test(ch)) return ch.toUpperCase();
        if (/[0-9]/.test(ch)) return "0-9";
        return "Other";
    }

    public setInputReadOnly(name: string, readOnly: boolean): void {
        if (!this.form) return;
        const el = this.form.elements.namedItem(name);
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            el.readOnly = !!readOnly;
        }
    }

    public getSuccessMessageKey(action: MasterDataEditSuccessAction): string {
        const actionKeyByResource: Record<string, Record<MasterDataEditSuccessAction, string>> = {
            brands: {
                saved: "master_data_brand_updated",
                deleted: "master_data_brand_deleted",
                uploaded: "master_data_upload_completed",
            },
            products: {
                saved: "master_data_product_updated",
                deleted: "master_data_product_deleted",
                uploaded: "master_data_product_image_uploaded",
            },
            users: {
                saved: "master_data_user_updated",
                deleted: "master_data_user_deleted",
                uploaded: "master_data_upload_completed",
            },
        };

        return actionKeyByResource[this.resource]?.[action] || "master_data_save_completed";
    }

    public updateCountryFlagPreview(): void {
        if (this.resource !== "brands") return;
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!(this.modal instanceof HTMLElement)) return;

        const preview = getRoleElement(this.modal, Roles.COUNTRY_FLAG_PREVIEW);
        if (!(preview instanceof HTMLElement)) return;

        const countryEl = this.form.elements.namedItem("country");
        const country = countryEl instanceof HTMLSelectElement ? countryEl.value || "" : "";
        const code = country ? this.countryFlagsMap[country] || "" : "";

        Array.from(preview.classList).forEach((cls) => {
            if (cls !== "fi" && cls.startsWith("fi-")) {
                preview.classList.remove(cls);
            }
        });

        if (code) {
            preview.classList.add(`fi-${code}`);
            preview.classList.add("is-visible");
        } else {
            preview.classList.remove("is-visible");
        }
    }

    public bindModalEvents(): void {
        if (this._modalEventsBound) return;
        this._modalEventsBound = true;

        if (this.overlay instanceof HTMLElement) {
            bindMasterDataClick(this.overlay, (e) => {
                if (this.mode === "edit" || this.mode === "create") {
                    e.preventDefault();
                    notifier.warn("modal_click_outside_to_close");
                    return;
                }

                this.closeModal();
            });
        }
        this.closeBtns.forEach((btn) => {
            bindMasterDataClick(btn, () => this.closeModal());
        });
        if (this.form instanceof HTMLFormElement) {
            if (this.form.dataset.masterDataSubmitBound !== "1") {
                bindMasterDataSubmit(this.form, this.submit);
                this.form.dataset.masterDataSubmitBound = "1";
            }

            if (!this.keyboardNav) {
                this.keyboardNav = attachEnterEscapeNavigation(this.form, {
                    escapeMode: "form",
                    isNextAllowedTarget: (el: Element) => {
                        return el.getAttribute("data-role") !== "url-check";
                    },
                });
            }
        }

        if (this.form instanceof HTMLFormElement && this.resource === "brands") {
            const imageEl = this.form.elements.namedItem("image_url");
            if (imageEl instanceof HTMLInputElement) {
                bindMasterDataInput(imageEl, () => this.updateImageUrlPreview());
            }

            const fileEl = this.form.elements.namedItem("brand_image");
            if (fileEl instanceof HTMLInputElement) {
                bindMasterDataChange(fileEl, async () => {
                    this.onBrandImageFileSelected();

                    if (this.mode !== "edit") return;
                    if (!this.getSelectedBrandImageFile()) return;
                    await this.uploadBrandImage();
                });
            }

            const pickBtn = getRoleButton(this.modal, Roles.BRAND_IMAGE_PICK);
            if (pickBtn instanceof HTMLButtonElement) {
                bindMasterDataClick(pickBtn, () => {
                    const input = this.form?.elements?.namedItem("brand_image");
                    if (input instanceof HTMLInputElement) input.click();
                });
            }
        }

        if (this.form instanceof HTMLFormElement && this.resource === "brands") {
            const brandUrlEl = this.form.elements.namedItem("website");
            if (brandUrlEl instanceof HTMLInputElement) {
                bindMasterDataInput(brandUrlEl, () => this.scheduleBrandUrlOgpUpdate(brandUrlEl.value));
                bindMasterDataBlur(brandUrlEl, () => void this.updateBrandUrlOgpPreview(brandUrlEl.value, true));
            }

            ["facebook", "instagram", "youtube", "tiktok", "x"].forEach((field) => {
                const el = this.form?.elements?.namedItem(field);
                if (!(el instanceof HTMLInputElement)) return;
                bindMasterDataInput(el, () => this.scheduleBrandSnsOgpUpdate(field, el.value));
                bindMasterDataBlur(el, () => void this.updateBrandSnsOgpPreview(field, el.value, true));
            });
        }

        if (this.form instanceof HTMLFormElement && this.resource === "products") {
            const imageEl = this.form.elements.namedItem("image");
            if (imageEl instanceof HTMLInputElement) {
                bindMasterDataInput(imageEl, () => this.updateProductImagePreview());
            }

            const fileEl = this.form.elements.namedItem("product_image");
            if (fileEl instanceof HTMLInputElement) {
                bindMasterDataChange(fileEl, async () => {
                    this.onProductImageFileSelected();

                    if (this.mode !== "edit") return;
                    if (!this.getSelectedProductImageFile()) return;
                    await this.uploadProductImage();
                });
            }

            const pickBtn = getRoleButton(this.modal, Roles.PRODUCT_IMAGE_PICK);
            if (pickBtn instanceof HTMLButtonElement) {
                bindMasterDataClick(pickBtn, () => {
                    const input = this.form?.elements?.namedItem("product_image");
                    if (input instanceof HTMLInputElement) input.click();
                });
            }
        }

        if (this.modal instanceof HTMLElement && this.resource === "products") {
            const pickerEl = getRoleElement(this.modal, Roles.PRODUCT_CATEGORY_PICKER);
            const sharedModal = SearchCategoryModalSetup.init(this.modal);

            if (pickerEl instanceof HTMLElement && sharedModal) {
                if (!this._productCategoryTree) {
                    this._productCategoryTree = this.getProductCategoryTreeFromDom();
                }

                if (!this._productCategoryModalBound) {
                    this._productCategoryModalBound = true;

                    bindMasterDataClick(pickerEl, (e) => {
                        const target = e.target;
                        const clearBtn = getClosest(target instanceof Element ? target : null, '[data-action="clear"]');
                        if (clearBtn) {
                            e.preventDefault();
                            e.stopPropagation();
                            setValue(this.form, "category_id", "");
                            this.syncProductCategoryHierarchy(null);
                            return;
                        }

                        this.openProductCategoryModal();
                    });

                    bindMasterDataKeydown(pickerEl, (e) => {
                        if (!(e instanceof KeyboardEvent)) return;
                        if (e.key !== "Enter" && e.key !== " ") return;
                        e.preventDefault();
                        this.openProductCategoryModal();
                    });
                }

                this.syncProductCategoryHierarchy();
            } else {
                const parentSelect = getRoleSelect(this.modal, Roles.PRODUCT_CATEGORY_PARENT_SELECT);
                const childSelect = getRoleSelect(this.modal, Roles.PRODUCT_CATEGORY_CHILD_SELECT);
                if (parentSelect instanceof HTMLSelectElement && childSelect instanceof HTMLSelectElement) {
                    if (!this._productCategoryTree) {
                        this._productCategoryTree = this.getProductCategoryTreeFromDom();
                    }

                    if (!this._productCategoryHierarchyBound) {
                        this._productCategoryHierarchyBound = true;
                        bindMasterDataChange(parentSelect, () => {
                            const pid = parentSelect.value ? Number(parentSelect.value) : null;
                            this.rebuildProductCategoryChildOptions(Number.isFinite(pid) ? pid : null);

                            if (pid) {
                                childSelect.value = String(pid);
                            } else {
                                childSelect.value = "";
                            }
                        });
                    }

                    this.syncProductCategoryHierarchy();
                }
            }
        }

        if (this.form instanceof HTMLFormElement && this.resource === "products") {
            const urlEl = this.form.elements.namedItem("product_url");
            if (urlEl instanceof HTMLInputElement) {
                bindMasterDataInput(urlEl, () => this.scheduleProductUrlOgpUpdate(urlEl.value));
                bindMasterDataBlur(urlEl, () => void this.updateProductUrlOgpPreview(urlEl.value, true));
            }
        }

        if (this.form instanceof HTMLFormElement && this.resource === "users" && !this._usersListenersBound) {
            this._usersListenersBound = true;
            registerMasterDataEditUsersListener(this);
        }

        if (this.form instanceof HTMLFormElement && this.resource === "brands") {
            const nameEl = this.form.elements.namedItem("brand_name");
            if (nameEl instanceof HTMLInputElement) {
                bindMasterDataInput(nameEl, () => {
                    const derived = this.deriveBrandIndexFromName(nameEl.value);
                    setValue(this.form, "brand_index", derived);
                });
            }

            const countryEl = this.form.elements.namedItem("country");
            if (countryEl instanceof HTMLSelectElement) {
                bindMasterDataChange(countryEl, () => this.updateCountryFlagPreview());
            }
        }

        if (this.modal instanceof HTMLElement && this.form instanceof HTMLFormElement) {
            const btns = getRoleElements(this.modal, Roles.URL_CHECK);
            btns.forEach((btnEl) => {
                if (!(btnEl instanceof HTMLButtonElement)) return;
                bindMasterDataClick(btnEl, () => {
                    const field = btnEl.getAttribute("data-field") || "";
                    if (!field) return;

                    const input = this.form?.elements.namedItem(field);
                    const raw = input instanceof HTMLInputElement ? input.value : "";
                    const url = this.normalizeUrlForCheck(field, raw);
                    if (!url) {
                        notifier.error("master_data_url_empty");
                        return;
                    }
                    this.openUrl(url);
                });
            });
        }
    }

    public isModelessMode(): boolean {
        return this.modal instanceof HTMLElement && this.modal.dataset.editorMode === "modeless";
    }

    public setModelessEditingState(isEditing: boolean): void {
        if (!this.isModelessMode()) return;
        if (!(this.modal instanceof HTMLElement)) return;

        const section = getClosest(this.modal, ".profile-basic-section");
        if (!(section instanceof HTMLElement)) return;
        section.classList.toggle("is-editing", isEditing);

        const panelsRoot = getClosest(section, ".user-profile-panels");
        if (panelsRoot instanceof HTMLElement) {
            panelsRoot.classList.toggle("is-basic-editing", isEditing);
        }
    }

    public openModal(): void {
        if (!this.modal) return;
        ModalNestedPosition.openModal({
            modalEl: this.modal,
            displayValue: "block",
        });

        if (!this.isModelessMode()) {
            ScrollLock.lock();
        }

        if (this.modal instanceof HTMLElement) {
            ChoicesControl.init(this.modal);
            OptionSelector.init(this.modal);
        }

        if (this.form instanceof HTMLFormElement && this.resource === "users") {
            registerMasterDataEditUsersListener(this);
        }

        this.setModelessEditingState(true);
    }

    public closeModal(): void {
        if (!this.modal) return;
        ModalNestedPosition.closeModal({ modalEl: this.modal });
        this.closeUserPrefectureModal();

        if (!this.isModelessMode()) {
            ScrollLock.unlock();
        }

        if (this.modal instanceof HTMLElement) {
            ChoicesControl.destroyAll(this.modal);
        }

        this.setModelessEditingState(false);

        this.form?.reset();
        this.keyboardNav?.clear();

        if (this.modal instanceof HTMLElement) {
            const imgUrl = getRoleImage(this.modal, Roles.IMAGE_URL_PREVIEW_IMG);
            const containerUrl = getRoleElement(this.modal, Roles.IMAGE_URL_PREVIEW);
            if (imgUrl instanceof HTMLImageElement) imgUrl.removeAttribute("src");
            if (containerUrl instanceof HTMLElement) {
                containerUrl.classList.remove("is-visible");
                containerUrl.classList.remove("is-broken");
            }

            const imgPath = getRoleImage(this.modal, Roles.IMAGE_PATH_PREVIEW_IMG);
            const containerPath = getRoleElement(this.modal, Roles.IMAGE_PATH_PREVIEW);
            if (imgPath instanceof HTMLImageElement) imgPath.removeAttribute("src");
            if (containerPath instanceof HTMLElement) {
                containerPath.classList.remove("is-visible");
                containerPath.classList.remove("is-broken");
            }

            this.clearProductUrlOgpPreview();
            this.clearBrandUrlOgpPreview();
            this.clearBrandSnsOgpPreviews();
            this.clearSelectedProductImageFile();
            this.updateProductImageUploadUi();
            this.clearSelectedBrandImageFile();
            this.updateBrandImageUploadUi();
        }
    }

    public revokeProductImageObjectUrl(): void {
        if (this.productImageObjectUrl) {
            URL.revokeObjectURL(this.productImageObjectUrl);
            this.productImageObjectUrl = "";
        }
    }

    public onProductImageFileSelected(): void {
        if (this.resource !== "products") return;
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!(this.modal instanceof HTMLElement)) return;

        const file = this.getSelectedProductImageFile();
        this.revokeProductImageObjectUrl();

        const ogpSection = getRoleElement(this.modal, Roles.PRODUCT_URL_OGP_SECTION);
        if (ogpSection instanceof HTMLElement) {
            ogpSection.style.display = file ? "none" : ogpSection.style.display;
        }

        if (!file) {
            this.updateProductImagePreview();
            return;
        }

        const container = getRoleElement(this.modal, Roles.IMAGE_PATH_PREVIEW);
        const img = getRoleImage(this.modal, Roles.IMAGE_PATH_PREVIEW_IMG);
        if (!(container instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

        const url = URL.createObjectURL(file);
        this.productImageObjectUrl = url;
        this.setImagePreview(container, img, url);
    }

    public revokeBrandImageObjectUrl(): void {
        if (this.brandImageObjectUrl) {
            URL.revokeObjectURL(this.brandImageObjectUrl);
            this.brandImageObjectUrl = "";
        }
    }

    public onBrandImageFileSelected(): void {
        if (this.resource !== "brands") return;
        if (!(this.form instanceof HTMLFormElement)) return;
        if (!(this.modal instanceof HTMLElement)) return;

        const file = this.getSelectedBrandImageFile();
        this.revokeBrandImageObjectUrl();

        if (!file) {
            this.updateImageUrlPreview();
            return;
        }

        const container = getRoleElement(this.modal, Roles.IMAGE_URL_PREVIEW);
        const img = getRoleImage(this.modal, Roles.IMAGE_URL_PREVIEW_IMG);
        if (!(container instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

        const url = URL.createObjectURL(file);
        this.brandImageObjectUrl = url;
        this.setImagePreview(container, img, url);
    }

    public fillFormForEdit(): void {
        if (!this.form) return;
        const item = asRecord(this.ctx.item);

        if (this.resource === "brands") {
            this.setInputReadOnly("brand_index", true);
            setValue(this.form, "brand_name", item.brand_name);
            setValue(this.form, "brand_status", item.brand_status);
            setValue(this.form, "brand_index", item.brand_index);
            setValue(this.form, "created_by", item.created_by);
            setValue(this.form, "updated_by", item.updated_by);
            setValue(this.form, "brand_note", item.brand_note);
            setValue(this.form, "image_url", item.image_url);
            setValue(this.form, "country", item.country);
            setValue(this.form, "website", item.website);
            setValue(this.form, "facebook", item.facebook);
            setValue(this.form, "instagram", item.instagram);
            setValue(this.form, "youtube", item.youtube);
            setValue(this.form, "tiktok", item.tiktok);
            setValue(this.form, "x", item.x || item.twitter);
            setValue(this.form, "priority", item.priority);
            setValue(this.form, "activate_time", toDatetimeLocalValue(item.activate_time));
            setValue(this.form, "brand_description_th", item.brand_description_th);
            setValue(this.form, "brand_description_en", item.brand_description_en);
            setValue(this.form, "brand_description_ja", item.brand_description_ja);

            this.updateCountryFlagPreview();
            this.updateImageUrlPreview();

            void this.updateBrandUrlOgpPreview(String(item.website || ""));
            void this.updateBrandSnsOgpPreview("facebook", String(item.facebook || ""));
            void this.updateBrandSnsOgpPreview("instagram", String(item.instagram || ""));
            void this.updateBrandSnsOgpPreview("youtube", String(item.youtube || ""));
            void this.updateBrandSnsOgpPreview("tiktok", String(item.tiktok || ""));
            void this.updateBrandSnsOgpPreview("x", String(item.x || item.twitter || ""));
        }

        if (this.resource === "products") {
            setValue(this.form, "product_status", item.product_status);
            setValue(this.form, "product_name", item.product_name);
            setValue(this.form, "product_url", item.product_url);
            setValue(this.form, "product_note", item.product_note);
            setValue(this.form, "created_by", item.created_by);
            setValue(this.form, "updated_by", item.updated_by);
            setValue(this.form, "image", item.image);
            setValue(this.form, "price", item.price);
            setValue(this.form, "price_unit", item.price_unit);
            const categoryId = item.category_id;
            this.syncProductCategoryHierarchy(typeof categoryId === "number" ? categoryId : Number(categoryId) || null);
            setValue(this.form, "brand_id", item.brand_id);
            setValue(this.form, "group_id", item.group_id);
            setValue(this.form, "unit", item.unit);
            setValue(this.form, "keyword", item.keyword);
            setValue(this.form, "manual_score_offset", item.manual_score_offset);
            setValue(this.form, "how_to_use", item.how_to_use);
            setValue(this.form, "ingredients", item.ingredients);
            setValue(this.form, "general_description", item.general_description);

            this.updateProductImagePreview();
            void this.updateProductUrlOgpPreview(String(item.product_url || ""));
        }

        if (this.resource === "users") {
            setValue(this.form, "nick_name", item.nick_name);
            setValue(this.form, "first_name", item.first_name);
            setValue(this.form, "middle_name", item.middle_name);
            setValue(this.form, "last_name", item.last_name);
            setValue(this.form, "email", item.email);
            setValue(this.form, "phone", item.phone);
            setValue(this.form, "date_of_birth", item.date_of_birth);
            setValue(this.form, "gender", item.gender);
            setValue(this.form, "zip_code", item.zip_code);
            setValue(this.form, "address", item.address);
            setValue(this.form, "prefecture", item.prefecture);
            this.renderUserPrefectureDisplay(String(item.prefecture || ""));
            setValue(this.form, "skin_type", item.skin_type);
            setValue(this.form, "skin_tone", item.skin_tone);
            setValue(this.form, "skin_concerns", item.skin_concerns);
            setValue(this.form, "hair_type", item.hair_type);
            setValue(this.form, "hair_scalp_concerns", item.hair_scalp_concerns);
            setValue(this.form, "beauty_wellness_interests", item.beauty_wellness_interests);
            setValue(this.form, "lifestyle_environment", item.lifestyle_environment);
            setValue(this.form, "makeup_frequency", item.makeup_frequency);
            setValue(this.form, "preferred_price_range", item.preferred_price_range);
            setValue(this.form, "frequent_category_ids", item.frequent_category_ids);
            setValue(this.form, "frequent_brand_ids", item.frequent_brand_ids);
            setValue(this.form, "social_instagram", item.social_instagram);
            setValue(this.form, "social_facebook", item.social_facebook);
            setValue(this.form, "social_x", item.social_x);
            setValue(this.form, "social_tiktok", item.social_tiktok);
            setValue(this.form, "social_youtube", item.social_youtube);
            setValue(this.form, "social_website", item.social_website);
        }
    }

    public async lookupUserAddressByZipCode(rawZipCode: string): Promise<void> {
        return lookupUserAddressByZipCodeForMasterDataEdit(this, rawZipCode);
    }

    public getUserPrefectureOptions(): ReturnType<typeof getUserPrefectureOptionsForMasterDataEdit> {
        return getUserPrefectureOptionsForMasterDataEdit(this);
    }

    public translateUserPrefectureLabel(
        entry: ReturnType<typeof getUserPrefectureOptionsForMasterDataEdit>[number] | null | undefined,
    ): string {
        return translateUserPrefectureLabelForMasterDataEdit(entry);
    }

    public getUserPrefectureLabel(value: string): string {
        return getUserPrefectureLabelForMasterDataEdit(this, value);
    }

    public getUserPrefectureOption(
        value: string,
    ): ReturnType<typeof getUserPrefectureOptionsForMasterDataEdit>[number] | null {
        return getUserPrefectureOptionForMasterDataEdit(this, value);
    }

    public normalizeUserPrefectureMatchingText(raw: string): string {
        return normalizeUserPrefectureMatchingTextForMasterDataEdit(raw);
    }

    public resolveUserPrefectureFromLookupPayload(payload: unknown): string {
        return resolveUserPrefectureFromLookupPayloadForMasterDataEdit(this, asRecord(payload));
    }

    public renderUserPrefectureDisplay(value: string): void {
        renderUserPrefectureDisplayForMasterDataEdit(this, value);
    }

    public renderUserPrefectureModalList(filterText = ""): void {
        renderUserPrefectureModalListForMasterDataEdit(this, filterText);
    }

    public openUserPrefectureModal(): void {
        openUserPrefectureModalForMasterDataEdit(this);
    }

    public closeUserPrefectureModal(): void {
        closeUserPrefectureModalForMasterDataEdit(this);
    }

    public clearUserPrefecturePendingValue(): void {
        clearUserPrefecturePendingValueForMasterDataEdit(this);
    }

    public applyUserPrefecturePendingValue(): void {
        applyUserPrefecturePendingValueForMasterDataEdit(this);
    }

    public fillFormForCreate(): void {
        if (!this.form) return;
        this.form.reset();

        const specDefaults = this.ctx?.specDefaults;
        if (specDefaults && typeof specDefaults === "object") {
            Object.entries(specDefaults).forEach(([key, value]) => {
                setValue(this.form, key, value);
            });
        }

        const defaults = this.ctx?.defaults;
        if (defaults && typeof defaults === "object") {
            Object.entries(defaults).forEach(([key, value]) => {
                setValue(this.form, key, value);
            });
        }

        if (this.resource === "products") {
            const categoryEl = this.form.elements.namedItem("category_id");
            const categoryValue =
                categoryEl instanceof HTMLSelectElement || categoryEl instanceof HTMLInputElement
                    ? categoryEl.value
                    : "";
            if (!categoryValue) {
                setValue(this.form, "category_id", "9999");
            }

            const el = this.form.elements.namedItem("category_id");
            const v =
                el instanceof HTMLSelectElement || el instanceof HTMLInputElement ? Number(el.value) || null : null;
            this.syncProductCategoryHierarchy(v);
        }

        if (this.resource === "brands") {
            this.setInputReadOnly("brand_index", true);
            const nameEl = this.form.elements.namedItem("brand_name");
            if (nameEl instanceof HTMLInputElement) {
                const derived = this.deriveBrandIndexFromName(nameEl.value);
                setValue(this.form, "brand_index", derived);
            }

            this.updateCountryFlagPreview();
            this.updateImageUrlPreview();

            const urlEl = this.form.elements.namedItem("website");
            if (urlEl instanceof HTMLInputElement) {
                void this.updateBrandUrlOgpPreview(urlEl.value);
            }
        }

        if (this.resource === "products") {
            const urlEl = this.form.elements.namedItem("product_url");
            if (urlEl instanceof HTMLInputElement) {
                void this.updateProductUrlOgpPreview(urlEl.value);
            }
        }
    }

    public openEdit(): void {
        if (!this.urls.update) return;
        this.mode = "edit";
        this.fillFormForEdit();
        this.updateProductImageUploadUi();
        this.updateBrandImageUploadUi();
        this.keyboardNav?.capture();
        this.openModal();
        this.keyboardNav?.focusFirstText();
    }

    public openCreate(): void {
        if (!this.urls.create) return;
        this.mode = "create";
        this.fillFormForCreate();
        this.updateProductImageUploadUi();
        this.updateBrandImageUploadUi();
        this.keyboardNav?.capture();
        this.openModal();
        this.keyboardNav?.focusFirstText();
    }

    public async destroy(): Promise<void> {
        if (!this.urls.delete) return;

        const ok = await notifier.confirm(this.getDeleteConfirmMessageKey());
        if (!ok) return;

        // Brands: the confirm dialog above already told the admin that
        // confirming also deletes this brand's products and posts (see
        // getDeleteConfirmMessageKey()), so cascade=1 always accompanies a
        // confirmed brand delete — there is no separate "delete brand only"
        // path, since an orphaned product/post left behind would be worse.
        const deleteUrl = this.resource === "brands" ? this.appendCascadeParam(this.urls.delete) : this.urls.delete;

        const response = await requestDeleteItem(deleteUrl);
        const json = await handleResponse(response, "master_data_delete_failed");
        if (!json) return;

        notifier.success(this.getSuccessMessageKey("deleted"));
        if (this.urls.afterDelete) {
            // Prefer the page the admin came from (e.g. a search results
            // page) over the generic index fallback, so a search query isn't
            // lost on delete — see ISS request "削除後、以前いたURL/検索クエリーに
            // リダイレクト".
            location.href = CommonUtils.resolveReturnUrl(resolveMasterUrl(String(this.urls.afterDelete)));
        } else {
            location.reload();
        }
    }

    private getDeleteConfirmMessageKey(): string {
        const keyByResource: Record<string, string> = {
            brands: "master_data_brand_delete_confirm",
            products: "master_data_product_delete_confirm",
            users: "master_data_user_delete_confirm",
        };

        return keyByResource[this.resource] || "master_data_product_delete_confirm";
    }

    private appendCascadeParam(url: string): string {
        const resolved = new URL(url, window.location.origin);
        resolved.searchParams.set("cascade", "1");

        return resolved.toString();
    }

    public async submit(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.form) return;

        const submitBtn = getSubmitControl(this.form);

        const raw = getFormDataObject(this.form);
        const payload = asRecord(
            normalizePayload(this.resource, raw, resolveMasterDataEditSchema(this.resource, this.mode) || undefined),
        );

        if (this.resource === "users") {
            const full = buildUserFullName(payload);
            if (full) {
                payload.full_name = full;
            } else {
                delete payload.full_name;
            }
            delete payload.name;
        }

        const validation = formValidator.validate(this.form, {
            showNotification: false,
            schema: resolveMasterDataEditSchema(this.resource, this.mode) || undefined,
            data: payload,
        });
        if (!validation.isValid) {
            const message = validation.firstError || "required_field";
            notifier.error(message, { duration: 5000 });
            return;
        }

        if (submitBtn instanceof HTMLButtonElement) {
            spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
        }

        try {
            let response: Response | undefined;
            if (this.mode === "create") {
                if (!this.urls.create) return;
                response = await requestCreateItem(this.urls.create, payload);
            } else {
                if (!this.urls.update) return;
                response = await requestUpdateItem(this.urls.update, payload);
            }

            const json = (await handleResponse(
                response,
                "master_data_save_failed",
            )) as MasterDataEditApiResponse | null;
            if (!json) return;

            notifier.success(this.getSuccessMessageKey("saved"));

            const item = asRecord(json.data?.item);

            if (this.resource === "products" && this.mode === "create" && Object.keys(item).length > 0) {
                this.ctx.item = item;
                this.updateProductImageUploadUi();

                const file = this.getSelectedProductImageFile();
                const productId = item.product_id;
                if (file && productId !== undefined && productId !== null && String(productId).trim() !== "") {
                    const uploadUrl = this.buildProductImageUploadUrl(productId);
                    if (uploadUrl) {
                        const fd = new FormData();
                        fd.append("product_image", file);

                        const uploadResponse = await requestUploadImage(uploadUrl, fd);
                        const uploadJson = (await handleResponse(
                            uploadResponse,
                            "master_data_upload_failed",
                        )) as MasterDataEditApiResponse | null;

                        if (uploadJson) {
                            const filename = String(uploadJson.data?.filename || "").trim();
                            if (filename) {
                                this.productImagePreviewBust = Date.now();
                                setValue(this.form, "image", filename);
                                const imageEl = this.form.elements.namedItem("image");
                                if (imageEl instanceof HTMLInputElement) {
                                    imageEl.dispatchEvent(new Event("input", { bubbles: true }));
                                }
                            }
                            notifier.success(this.getSuccessMessageKey("uploaded"));
                        }
                    } else {
                        notifier.error("master_data_upload_endpoint_missing");
                    }

                    this.clearSelectedProductImageFile();
                }
            }

            if (this.resource === "brands" && this.mode === "create" && Object.keys(item).length > 0) {
                this.ctx.item = item;
                this.updateBrandImageUploadUi();

                const file = this.getSelectedBrandImageFile();
                const brandId = item.brand_id;
                if (file && brandId !== undefined && brandId !== null && String(brandId).trim() !== "") {
                    const uploadUrl = this.buildBrandImageUploadUrl(brandId);
                    if (uploadUrl) {
                        const fd = new FormData();
                        fd.append("brand_image", file);

                        const uploadResponse = await requestUploadImage(uploadUrl, fd);
                        const uploadJson = (await handleResponse(
                            uploadResponse,
                            "master_data_upload_failed",
                        )) as MasterDataEditApiResponse | null;

                        if (uploadJson) {
                            const filename = String(uploadJson.data?.filename || "").trim();
                            if (filename) {
                                this.brandImagePreviewBust = Date.now();
                                setValue(this.form, "image_url", filename);
                                const imageEl = this.form.elements.namedItem("image_url");
                                if (imageEl instanceof HTMLInputElement) {
                                    imageEl.dispatchEvent(new Event("input", { bubbles: true }));
                                }
                            }
                            notifier.success(this.getSuccessMessageKey("uploaded"));
                        }
                    } else {
                        notifier.error("master_data_upload_endpoint_missing");
                    }

                    this.clearSelectedBrandImageFile();
                }
            }

            if (this.mode === "create" && item && this.urls.showBase) {
                const idKey = this.resource === "brands" ? "brand_id" : "product_id";
                const newId = item[idKey];
                if (newId !== undefined && newId !== null) {
                    location.href = resolveMasterUrl(`${this.urls.showBase}/${newId}`);
                    return;
                }
            }

            location.reload();
        } finally {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }

    public async uploadProductImage(): Promise<void> {
        if (this.resource !== "products") return;
        if (!(this.form instanceof HTMLFormElement)) return;

        const productId = asRecord(this.ctx?.item).product_id;
        if (productId === null || productId === undefined || String(productId).trim() === "") {
            notifier.error("master_data_save_before_product_upload");
            return;
        }

        const uploadUrl = this.buildProductImageUploadUrl(productId);
        if (!uploadUrl) {
            notifier.error("master_data_upload_endpoint_missing");
            return;
        }

        const file = this.getSelectedProductImageFile();
        if (!file) {
            notifier.error("master_data_select_image_first");
            return;
        }

        const fd = new FormData();
        fd.append("product_image", file);

        const response = await requestUploadImage(uploadUrl, fd);
        const json = (await handleResponse(response, "master_data_upload_failed")) as MasterDataEditApiResponse | null;
        if (!json) return;

        const filename = String(json.data?.filename || "").trim();
        if (filename) {
            this.productImagePreviewBust = Date.now();
            setValue(this.form, "image", filename);
            const imageEl = this.form.elements.namedItem("image");
            if (imageEl instanceof HTMLInputElement) {
                imageEl.dispatchEvent(new Event("input", { bubbles: true }));
            }
        }

        this.clearSelectedProductImageFile();
        this.updateProductImageUploadUi();
        notifier.success(this.getSuccessMessageKey("uploaded"));
    }

    public buildProductImageUploadUrl(productId: unknown): string {
        const explicit = String(this.urls?.uploadImage || "").trim();
        if (explicit) return explicit;

        const base = String(this.urls?.uploadImageBase || "")
            .trim()
            .replace(/\/$/, "");
        if (!base) return "";

        const id = String(productId ?? "").trim();
        if (!id) return "";

        return `${base}/${id}/image`;
    }

    public getSelectedProductImageFile(): File | null {
        if (!(this.form instanceof HTMLFormElement)) return null;
        const fileEl = this.form.elements.namedItem("product_image");
        if (!(fileEl instanceof HTMLInputElement) || !fileEl.files || fileEl.files.length === 0) return null;
        return fileEl.files[0] || null;
    }

    public clearSelectedProductImageFile(): void {
        if (!(this.form instanceof HTMLFormElement)) return;
        const fileEl = this.form.elements.namedItem("product_image");
        if (fileEl instanceof HTMLInputElement) {
            fileEl.value = "";
        }

        if (this.resource === "products") {
            this.revokeProductImageObjectUrl();
        }
    }

    public updateProductImageUploadUi(): void {
        // Upload is triggered automatically on file selection.
    }

    public async uploadBrandImage(): Promise<void> {
        if (this.resource !== "brands") return;
        if (!(this.form instanceof HTMLFormElement)) return;

        const brandId = asRecord(this.ctx?.item).brand_id;
        if (brandId === null || brandId === undefined || String(brandId).trim() === "") {
            notifier.error("master_data_save_before_brand_upload");
            return;
        }

        const uploadUrl = this.buildBrandImageUploadUrl(brandId);
        if (!uploadUrl) {
            notifier.error("master_data_upload_endpoint_missing");
            return;
        }

        const file = this.getSelectedBrandImageFile();
        if (!file) {
            notifier.error("master_data_select_image_first");
            return;
        }

        const fd = new FormData();
        fd.append("brand_image", file);

        const response = await requestUploadImage(uploadUrl, fd);
        const json = (await handleResponse(response, "master_data_upload_failed")) as MasterDataEditApiResponse | null;
        if (!json) return;

        const filename = String(json.data?.filename || "").trim();
        if (filename) {
            this.brandImagePreviewBust = Date.now();
            setValue(this.form, "image_url", filename);
            const imageEl = this.form.elements.namedItem("image_url");
            if (imageEl instanceof HTMLInputElement) {
                imageEl.dispatchEvent(new Event("input", { bubbles: true }));
            }
        }

        this.clearSelectedBrandImageFile();
        this.updateBrandImageUploadUi();
        notifier.success(this.getSuccessMessageKey("uploaded"));
    }

    public buildBrandImageUploadUrl(brandId: unknown): string {
        const explicit = String(this.urls?.uploadImage || "").trim();
        if (explicit) return explicit;

        const base = String(this.urls?.uploadImageBase || "")
            .trim()
            .replace(/\/$/, "");
        if (!base) return "";

        const id = String(brandId ?? "").trim();
        if (!id) return "";

        return `${base}/${id}/image`;
    }

    public getSelectedBrandImageFile(): File | null {
        if (!(this.form instanceof HTMLFormElement)) return null;
        const fileEl = this.form.elements.namedItem("brand_image");
        if (!(fileEl instanceof HTMLInputElement) || !fileEl.files || fileEl.files.length === 0) return null;
        return fileEl.files[0] || null;
    }

    public clearSelectedBrandImageFile(): void {
        if (!(this.form instanceof HTMLFormElement)) return;
        const fileEl = this.form.elements.namedItem("brand_image");
        if (fileEl instanceof HTMLInputElement) {
            fileEl.value = "";
        }

        if (this.resource === "brands") {
            this.revokeBrandImageObjectUrl();
        }
    }

    public updateBrandImageUploadUi(): void {
        // Upload is triggered automatically on file selection.
    }
}
